import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { interval, Subscription, of, firstValueFrom } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import {
  BulkCreatePayload,
  MessageWithHeader,
} from '../../../core/interfaces/notification.inerface';
import { NotificationService } from '../../../core/services/notification.service';
import { catchError, switchMap } from 'rxjs/operators';
import { MessagingService } from '../../../core/services/messaging.service';
import { ArcAngelService } from '../../../core/services/arc-angel.service';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import { OrganizationService } from '../../../core/services/organization.service';

interface Message {
  text: string;
  isMe: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-message',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './message.component.html',
  styleUrl: './message.component.css',
})
export class MessageComponent implements OnInit, OnDestroy {
  @Input() user: any;
  @Input() title = 'Chat';
  private pollSub?: Subscription;
  messages: Message[] = [];
  newMessage = '';
  userId!: string;
  userName!: string;
  userOrg!: string;
  messageListFull: any[] = [];
  numberTotal = 0;
  lastMessageTime: string | null = null;
  selectedOrg = '';
  isLoading = false;
  organizations: number[] = [];
  organizationsList: any[] = [];
  users: any[] = [];
  isOrgVisible = false; // default false
  userDetailsFromUserInfo: any;
  constructor(
    public activeModal: NgbActiveModal,
    private notificationService: NotificationService,
    private messagingService: MessagingService,
    private arcAngelService: ArcAngelService,
    private orgUtil: OrganizationUtilService,
    private organizationService: OrganizationService
  ) {}
  ngOnInit(): void {
    this.userId = localStorage.getItem('PAA_USER_ID') ?? '';
    this.userName = localStorage.getItem('PAA_Name') ?? '';
    this.userOrg = localStorage.getItem('PAA_org') ?? '';
    this.organizationService
      .getAllUsersWithOrganization()
      .subscribe((users) => {
        this.users = users ?? []; // Store users in a property
      });
    this.orgDetails();
    this.startPolling();
    if (this.userDetailsFromUserInfo) {
      this.user = this.userDetailsFromUserInfo;
    }
  }

  async orgDetails(): Promise<void> {
    try {
      const twilioOrgIds: string[] = await firstValueFrom(
        this.arcAngelService.getTwilioOrgs()
      );
      if (!twilioOrgIds || twilioOrgIds.length === 0) {
        this.activeModal.close();
        return;
      }
      if (twilioOrgIds.length === 1) {
        this.selectedOrg = twilioOrgIds[0];
        this.organizations = twilioOrgIds.map(Number);
      }
      if (twilioOrgIds.length > 1) {
        const selOrgId = this.runChooseOrgDialog(twilioOrgIds);
        if (selOrgId) {
          this.selectedOrg = String(selOrgId);
        }
        this.isOrgVisible = true;
      }
      this.organizationsList = twilioOrgIds.map(
        (orgId) => this.orgUtil.myOrgs[orgId]
      );
    } catch (error) {
      console.error('Error choosing orgId:', error);
      this.activeModal.close();
    } finally {
      this.isOrgVisible = true; // Only after everything is done
    }
  }

  runChooseOrgDialog(orgIds: string[]): any {
    this.organizationsList = orgIds.map((orgId) => this.orgUtil.myOrgs[orgId]);
    if (orgIds.length === 1) {
      return of(String(orgIds[0]));
    }
    this.organizations = this.organizationsList.map((org: any) =>
      Number(org.id)
    );
  }

  startPolling(): void {
    this.pollSub = interval(2000).subscribe(() => {
      this.updateMessagesForUser().subscribe({
        next: (userMessages) => {
        },
        error: (err) => console.error('User messages polling error:', err),
      });

      // Call #2: org messages
      this.messagingService
        .getNewMessagesForOrgs(this.organizations, [])
        .subscribe({
          next: (orgMessages) => {
          },
          error: (err) => console.error('Org messages polling error:', err),
        });
    });
  }

  ngOnDestroy(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  stopPolling() {
    this.pollSub?.unsubscribe();
  }

  selectOrg(org: string) {
    this.selectedOrg = org;
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.selectedOrg) return;
    this.isLoading = true;

    const payload: MessageWithHeader = {
      subject: '',
      toUserId: [this.user.id],
      userId: this.userId,
      methods: ['SMS'],
      text: this.newMessage,
      html: this.newMessage,
      entity: 'USER',
    };

    this.notificationService.sendSMSandEMAIL(payload).subscribe(
      (res) => {
        if (res && res.id) {
          this.newMessage = '';
          const bulkCreatePayload: BulkCreatePayload = {
            messageId: res.id,
            toUserIds: [this.user?.userId],
            fromOrganizationId: this.selectedOrg,
          };
          this.notificationService
            .bulkCreateToUsersAndOrganizations(bulkCreatePayload, res.id)
            .subscribe(
              () => {
                this.notificationService
                  .sendMessageFromOrganization(res.id)
                  .pipe(
                    catchError((error) => {
                      this.isLoading = false;
                      return of(null);
                    })
                  )
                  .subscribe((sendRes) => {
                    this.newMessage = '';
                    this.isLoading = false;
                    if (sendRes) {
                      this.newMessage = '';
                      this.updateMessagesForUser();
                      //AlertHelper.success('Your message was sent from the organization successfully.', 'Message Sent!');
                    }
                  });
              },
              (sendErr) => {
                this.isLoading = false;
              }
            );
        } else {
          this.isLoading = false;
          console.error('No ID found in the response from sendSMSandEMAIL.');
        }
      },
      (err) => {
        this.isLoading = false;
        console.error('Error sending message:', err);
      }
    );
  }

  updateMessagesForUser() {
    return this.messagingService
      .getNewMessagesForUserAndOrg(
        this.organizations,
        [Number(this.user.userId)],
        ['SMS'],
        10,
        1
      )
      .pipe(
        switchMap((result: any) => {
          if (!result || !result.rows || result.rows.length === 0) {
            return of(null);
          }

          const rows = (result.rows || result.data?.rows || []).reverse();
          const formattedRows = rows
            .filter(
              (row: any) =>
                Array.isArray(row.methods) && row.methods.includes('SMS')
            )
            .map((row: any) => ({
              ...row,
              isMe: row.fromUserId === Number(this.user.userId),
              timestamp: row.createdAt || row.timestamp || row.created_at,
            }));

          // Call reformat function
          const reformatted = this.reformat(formattedRows);

          // update localTime before assigning
          const updated = reformatted.map((msg) => ({
            ...msg,
            localTime: this.getMessageLocalTime(msg),
          }));

          this.messageListFull = updated;

          this.lastMessageTime =
            rows[0]?.createdAt || rows[0]?.created_at || rows[0]?.timestamp;
          this.numberTotal += rows.length;

          this.markAsRead(rows);

          setTimeout(() => {
            const elem = document.getElementById('messagingbox-main');
            if (elem) elem.scrollTop = elem.scrollHeight;
          }, 500);

          return of(null);
        }),
        catchError((error) => {
          console.error('Failed to fetch messages:', error);
          return of(null);
        })
      );
  }

  private reformat(messages: any[]): any[] {
    return this.orgUtil.reformat(messages, this.users, this.organizationsList);
  }

  private markAsRead(messageList: any[]) {
    const ids = messageList.flatMap((o: any) =>
      (o.ToMessageHeaders || [])
        .filter((h: any) => h.organizationId && h.readAt === null)
        .map((h: any) => h.id)
    );

    if (ids.length) {
      this.messagingService.markMessagesAsRead(ids);
    }
  }

  public getMessageLocalTime(msg: any): string {
    const timestamp =
      msg?.ToMessageHeaders?.[0]?.readAt ?? msg?.created_at ?? msg?.createdAt;

    if (!timestamp) return '';

    const date = new Date(timestamp);

    return date
      .toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(',', '') // remove comma between date and time
      .toLowerCase(); // lowercase am/pm
  }

  get selectedOrgName(): string {
    const org = this.organizationsList.find(
      (o) => String(o.id) === this.selectedOrg
    );
    return org ? org.name : '';
  }

  onEnter(event: KeyboardEvent): void {
    if (event.ctrlKey || event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }
}
